import { createContext, useCallback, useContext, useEffect, useState } from 'react'

type Theme = 'light' | 'dark'

interface ThemeContextType {
    theme: Theme
    toggleTheme: () => void
    isDark: boolean
    isHighContrast: boolean
    toggleHighContrast: () => void
}

const ThemeContext = createContext<ThemeContextType>({} as ThemeContextType)

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [theme, setTheme] = useState<Theme>(() => {
        const stored = localStorage.getItem('theme') as Theme | null
        if (stored) return stored
        return 'light'
    })

    const [isHighContrast, setIsHighContrast] = useState<boolean>(() => {
        return localStorage.getItem('highContrast') === 'true'
    })

    useEffect(() => {
        const root = document.documentElement
        if (theme === 'dark') {
            root.classList.add('dark')
        } else {
            root.classList.remove('dark')
        }
        localStorage.setItem('theme', theme)
    }, [theme])

    useEffect(() => {
        const root = document.documentElement
        if (isHighContrast) {
            root.classList.add('high-contrast')
        } else {
            root.classList.remove('high-contrast')
        }
        localStorage.setItem('highContrast', String(isHighContrast))
    }, [isHighContrast])

    const toggleTheme = useCallback(() => {
        setTheme(prev => (prev === 'light' ? 'dark' : 'light'))
    }, [])

    const toggleHighContrast = useCallback(() => {
        setIsHighContrast(prev => !prev)
    }, [])

    return (
        <ThemeContext.Provider value={{
            theme,
            toggleTheme,
            isDark: theme === 'dark',
            isHighContrast,
            toggleHighContrast
        }}>
            {children}
        </ThemeContext.Provider>
    )
}

/**
 * Hook para acessar o tema.
 * @example
 * const { theme, toggleTheme, isDark } = useTheme()
 */
export function useTheme() {
    const context = useContext(ThemeContext)
    if (!context.toggleTheme) {
        throw new Error('useTheme must be used within a ThemeProvider')
    }
    return context
}
